import type { CargoDto, JobStopDto, ShipmentDto } from '../api/adminServices'

export type MapJobStop = {
  key: string
  label: string
  detail?: string
  latitude: number
  longitude: number
  type: 'pickup' | 'delivery'
  /** 1-based order along the route (pickup → dropoffs). */
  sequence: number
}

const PICKUP_STOP_TYPES = new Set(['PICKUP', 'EXCHANGE_PICKUP'])

function isValidCoord(lat: unknown, lng: unknown): boolean {
  const la = Number(lat)
  const ln = Number(lng)
  return Number.isFinite(la) && Number.isFinite(ln) && la !== 0 && ln !== 0
}

/** VRPTW stop sequence from job offer — preferred when available (matches Driver Foreman map). */
export function buildStopsFromJobStops(jobStops: JobStopDto[]): MapJobStop[] {
  const sorted = [...jobStops].sort((a, b) => a.sequence - b.sequence)
  const out: MapJobStop[] = []

  sorted.forEach((stop, index) => {
    if (!isValidCoord(stop.latitude, stop.longitude)) return
    const isPickup = PICKUP_STOP_TYPES.has(String(stop.type || '').toUpperCase())
    out.push({
      key: `stop-${stop.id}`,
      label: stop.businessName || stop.address || `Stop ${index + 1}`,
      detail: stop.address || undefined,
      latitude: Number(stop.latitude),
      longitude: Number(stop.longitude),
      type: isPickup ? 'pickup' : 'delivery',
      sequence: index + 1,
    })
  })

  return out
}

type CargoWithDropoff = CargoDto & {
  dropoffLatitude?: number
  dropoffLongitude?: number
  dropoffAddress?: string
  dropoffBusinessName?: string
  dropoffContactName?: string
  sequenceNumber?: number
}

/** Pickup + per-cargo dropoffs for all shipments — aligned with Shipper live-location map stops. */
export function buildStopsFromShipments(shipments: ShipmentDto[]): MapJobStop[] {
  const drafts: Omit<MapJobStop, 'sequence'>[] = []

  for (const shipment of shipments) {
    if (isValidCoord(shipment.pickupLatitude, shipment.pickupLongitude)) {
      drafts.push({
        key: `${shipment.id}-pickup`,
        label: shipment.pickupAddress || 'Pickup',
        detail: shipment.pickupContactName,
        latitude: Number(shipment.pickupLatitude),
        longitude: Number(shipment.pickupLongitude),
        type: 'pickup',
      })
    }

    for (const rawCargo of shipment.cargos ?? []) {
      const cargo = rawCargo as CargoWithDropoff
      const lat = cargo.dropoffLatitude ?? cargo.deliveryLatitude
      const lng = cargo.dropoffLongitude ?? cargo.deliveryLongitude
      if (!isValidCoord(lat, lng)) continue

      drafts.push({
        key: `${shipment.id}-cargo-${cargo.id ?? drafts.length}`,
        label:
          cargo.dropoffBusinessName ||
          cargo.dropoffAddress ||
          cargo.deliveryAddress ||
          `Delivery ${cargo.sequenceNumber ?? ''}`.trim(),
        detail: cargo.dropoffAddress || cargo.deliveryAddress,
        latitude: Number(lat),
        longitude: Number(lng),
        type: 'delivery',
      })
    }
  }

  return drafts.map((stop, index) => ({ ...stop, sequence: index + 1 }))
}

/** Numbered circle marker — same visual language as Shipper live-location map. */
export function buildStopMarkerDataUri(color: string, sequence: number): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">` +
    `<circle cx="20" cy="20" r="15" fill="${color}" stroke="#ffffff" stroke-width="3"/>` +
    `<text x="20" y="21" text-anchor="middle" dominant-baseline="middle" ` +
    `fill="#ffffff" font-size="14" font-weight="bold" font-family="system-ui,sans-serif">${sequence}</text>` +
    `</svg>`
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

export function stopMarkerColor(type: MapJobStop['type']): string {
  return type === 'pickup' ? '#2196F3' : '#FF9800'
}
